/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, December 23rd 2021, 10:13:59 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { PreBuiltService } from "lib/schemas";
import { iso8601DateToDateString, numberToString } from "lib/utils";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import EmptyListWrapper from "ui-widgets/wrappers/EmptyListWrapper";
import { HorizontalStackWidget, OSActionButton } from "ui-widgets";
import { SuccessCheckButton } from "ui-widgets";
import { OSBackButton } from "ui-widgets";
import { InstallationButtonWrapper } from "./InstallationButtonWrapper";

interface PreBuiltServiceViewComponentProps {
  prebuilt_service: PreBuiltService;
  handleBackClick: () => void;
}

interface IPreviewImageState {
  index: number;
  image: string;
}

export const PreBuiltServiceViewComponent = ({
  prebuilt_service,
  handleBackClick,
}: PreBuiltServiceViewComponentProps) => {
  const previewImages = [prebuilt_service.cover_art, ...prebuilt_service.screenshots];
  const [previewImageState, setPreviewImageState] = React.useState<IPreviewImageState>({
    index: 0,
    image: previewImages[0],
  });

  return (
    <div className="os-preview-prebuilt-service">
      <Grid container spacing={2}>
        <Grid item md={6} className="os-prebuilt-service-preview-1">
          <div className="os-prebuilt-service-preview-image">
            <ImageListItem key={`main-preview-image-${prebuilt_service.id}`}>
              <img
                src={`${previewImageState.image}`}
                srcSet={`${previewImageState.image}`}
                alt={prebuilt_service.title}
                loading="lazy"
              />
            </ImageListItem>

            <EmptyListWrapper
              items={previewImages}
              noDataMessage="This template does not have any screenshots available."
            >
              <ImageList sx={{ width: 200, height: 60 }} cols={3} rowHeight={100}>
                {previewImages.map((screenshot, index) => (
                  <ImageListItem
                    className={`os-prebuilt-service-screenshot ${previewImageState.index === index ? "active" : ""}`}
                    key={`${prebuilt_service.id}-screenshot-${index}`}
                    onClick={() => setPreviewImageState({ index, image: screenshot })}
                    style={{ cursor: "pointer" }}
                  >
                    <img
                      src={`${screenshot}`}
                      srcSet={`${screenshot}`}
                      alt={`${prebuilt_service.title} Screenshot`}
                      loading="lazy"
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </EmptyListWrapper>
          </div>
        </Grid>
        <Grid item md={6} className="os-prebuilt-service-preview-2">
          <HorizontalStackWidget className="os-prebuilt-service-preview-21">
            <div className="os-os-preview-prebuilt-service-content os-prebuilt-service-preview-211">
              <div className="os-preview-prebuilt-service-header">
                <div className="os-preview-prebuilt-service-header-1">
                  <div className="os-preview-prebuilt-service-back-arrow">
                    <OSBackButton onClick={handleBackClick} />
                  </div>
                  <div className="os-preview-prebuilt-service-title">
                    <Typography variant="h4">{`${prebuilt_service.title}`}</Typography>
                    <Typography variant="h5">{`${prebuilt_service.category_name}`}</Typography>
                    <Typography className="os-service-meta">
                      Published: {iso8601DateToDateString(prebuilt_service.date_published)}
                      {prebuilt_service.organizations_using_count > 0 && (
                        <React.Fragment>
                          {" "}
                          <span>|</span>
                          Installed by {numberToString(prebuilt_service.organizations_using_count, 1)} organization(s)
                        </React.Fragment>
                      )}
                    </Typography>
                  </div>
                </div>
                <div className="os-preview-prebuilt-service-header-2">
                  <div className="os-preview-prebuilt-service-install-actions">
                    <div className="os-service-card-summary-action d-flex">
                      <div className="os-service-card-summary-action-status">
                        {prebuilt_service.is_installed ? <SuccessCheckButton> Installed</SuccessCheckButton> : null}
                      </div>
                      <InstallationButtonWrapper
                        isAlreadyInstalled={prebuilt_service.is_installed}
                        prebuiltServiceId={prebuilt_service.id}
                        serviceTitle={prebuilt_service.title}
                        serviceCode={prebuilt_service.code}
                      >
                        {({ handleInstall }) => (
                          <OSActionButton onClick={handleInstall} variant="contained" size="large">
                            {prebuilt_service.is_installed ? "Update Template" : "Install Template"}
                          </OSActionButton>
                        )}
                      </InstallationButtonWrapper>
                    </div>
                  </div>
                </div>
              </div>

              <div className="os-preview-prebuilt-service-main">
                <Typography className="os-prebuilt-service-preview-description" variant="body1">
                  <p>{prebuilt_service.description}</p>
                </Typography>
              </div>
            </div>
          </HorizontalStackWidget>
        </Grid>
      </Grid>
    </div>
  );
};
